import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { Modal } from 'antd';
import { AxiosResponse } from 'axios';
import { useIntl } from 'react-intl';
import AddressForm from 'src/components/account/addresses/AddressForm';
import CheckoutCard, {
  SelectorIconEnum,
} from 'src/components/experienceCheckout/CheckoutCard';
import SelectorWrapper from 'src/components/general/ScrollableSelector';
import { CheckoutTypeEnum } from 'src/shared/enums';
import { IAddressData, ICreateAddress } from 'src/shared/models';
import {
  createAddress,
  customFormatMessage,
  getRegionName,
  showErrorNotification,
  showSuccessNotification,
  updateAddress,
} from 'utils';
import { useGetAddressConfig } from '../account/addresses/useGetAddressConfig';

interface OwnProps {
  addressList: IAddressData[];
  setAddressList: Dispatch<SetStateAction<IAddressData[]>>;
  selectedAddress?: IAddressData;
  setSelectedAddress: Dispatch<SetStateAction<IAddressData>>;
  type?: number;
}

export default function CheckoutAddressSelector({
  addressList,
  selectedAddress,
  setAddressList,
  setSelectedAddress,
  type,
}: OwnProps) {
  const [modalVisible, setModalVisible] = useState(false);
  const { departmentList, districtList, provinceList, regionList } =
    useGetAddressConfig();

  const { formatMessage } = useIntl();
  const intl = useIntl();

  useEffect(() => {
    if (type !== CheckoutTypeEnum.USER_ADDRESS_SHIPPING) return;

    if (!addressList?.length) return;

    const userAddress =
      addressList.find(({ defaultAddress }) => defaultAddress) ||
      addressList[0];

    setSelectedAddress(userAddress);
  }, []);

  const handleCancelForm = () => {
    setModalVisible(false);
  };

  const handleCreate = async (address: IAddressData) => {
    try {
      const response: AxiosResponse<ICreateAddress> = await createAddress(
        address,
      );
      setAddressList(response.data.addressList);
      setSelectedAddress(response.data.address);
      showSuccessNotification('create', intl);
    } catch (err) {
      showErrorNotification('create', intl);
      console.error({ err });
    } finally {
      setModalVisible(false);
    }
  };

  const handleUpdate = async (address: IAddressData) => {
    try {
      const newList: AxiosResponse<IAddressData[]> = await updateAddress(
        address,
      );
      setAddressList(newList.data);
      setSelectedAddress(
        newList.data.find(({ idAddress }) => idAddress === address.idAddress) ||
          address,
      );
      showSuccessNotification('update', intl);
    } catch (err) {
      showErrorNotification('update', intl);
      console.error({ err });
    } finally {
      setModalVisible(false);
    }
  };

  const renderElement = (record: IAddressData) => {
    const handleAction = () => {
      setSelectedAddress(record);
    };

    const handleClickExtra = () => {
      setSelectedAddress(record);
      setModalVisible(true);
    };

    return (
      <CheckoutCard
        action={handleAction}
        description={customFormatMessage(
          'page.experience.checkout.card-content',
          {
            address: record.address.includes('#')
              ? record.address.substring(0, record.address.indexOf('#'))
              : record.address,
            regionName: getRegionName(regionList, record.regionName),
            zipCode: record.zipCode,
            city: record.city,
            district: record.district,
          },
        )}
        extra={{ action: handleClickExtra }}
        icon={SelectorIconEnum.HOME_ADDRESS}
        header={
          record.defaultAddress &&
          formatMessage({ id: 'page.experience.checkout.card-title' })
        }
        key={record.idAddress}
        id={record.idAddress}
        selected={selectedAddress?.idAddress === record.idAddress}
        title={`${record.name} ${record.surname}`}
      />
    );
  };
  const extraElement = () => {
    const action = () => {
      setSelectedAddress(null);
      setModalVisible(true);
    };

    return (
      <CheckoutCard
        key="extra-selector"
        id="extra-selector"
        action={action}
        icon={SelectorIconEnum.ADD_CIRCLE}
        title={formatMessage({ id: 'page.account.addresses.add' })}
        selected={false}
      />
    );
  };

  return (
    <SelectorWrapper
      data={addressList
        .filter(({ status }) => status)
        .sort((a, b) =>
          a.defaultAddress === b.defaultAddress ? 0 : a.defaultAddress ? -1 : 1,
        )}
      render={renderElement}
      extraElement={extraElement}>
      <Modal
        className="address-modal checkout"
        visible={modalVisible}
        onCancel={handleCancelForm}
        closable={true}
        footer={false}
        destroyOnClose={true}>
        <AddressForm
          address={selectedAddress}
          isEditForm={Boolean(selectedAddress?.idAddress)}
          isFirstAddress={!addressList}
          onCreateAddress={handleCreate}
          onUpdateAddress={handleUpdate}
          regionList={regionList}
          departmentList={departmentList}
          provinceList={provinceList}
          districtList={districtList}
        />
      </Modal>
    </SelectorWrapper>
  );
}
